import type { ReadonlyObservableArray } from "ecce-react";
import type { Prompt } from "../../prompt/prompt";
import type { Services } from "../services";
import type { AlertPromptConfig } from "./alert-prompt";
import type { ConfirmationPromptConfig } from "./confirmation-prompt";
import { ObservableArray, observable } from "ecce-react";
import { notNullish } from "../../util/nullish";
import { AlertPrompt } from "./alert-prompt";
import { ConfirmationPrompt } from "./confirmation-prompt";
import { ConfirmDiscardUnsavedChangesPrompt } from "./confirm-discard-unsaved-changes-prompt";


export class ActivePrompt<T = unknown> {
	static #NEXT_ID = 0;

	readonly id = ActivePrompt.#NEXT_ID++;

	@observable() accessor prompt: Prompt<T> | null;

	constructor(prompt: Prompt<T>) {
		this.prompt = prompt;
	}
}


export class PromptService {
	readonly #activePrompts = new ObservableArray<ActivePrompt>();

	#services: Services | null = null;

	/** Called by Services */
	private initialise(services: Services): void {
		this.#services = services;

		this.#services.navigation.on("navigate", () => {
			for(const prompt  of this.#activePrompts) {
				prompt.prompt?.dispose();
			}

			this.#activePrompts.clear();
		});
	}

	show<T>(prompt: Prompt<T>): Promise<T> {
		if(!this.#services) {
			throw new Error("PromptService was not initialised.");
		}

		const services = this.#services;
		if(this.#isPromptActive(prompt)) {
			throw new Error("That prompt is already shown!");
		}


		const activePrompt = new ActivePrompt(prompt);
		this.#activePrompts.push(activePrompt as ActivePrompt<unknown>);

		return new Promise(resolve => {
			prompt["initPrompt"](services, result => {
				const nextPrompts: ActivePrompt[] = this.#activePrompts.toArray()
					.filter(p => notNullish(p.prompt));
				this.#activePrompts.replace(nextPrompts);

				activePrompt.prompt = null;

				prompt["dispose"]();
				resolve(result);
			});
		});
	}

	#isPromptActive(prompt: Prompt<any>): boolean { // eslint-disable-line @typescript-eslint/no-explicit-any
		return !!this.#activePrompts.find(p => p.prompt === prompt);
	}

	get activePrompts(): ReadonlyObservableArray<Readonly<ActivePrompt>> {
		return this.#activePrompts;
	}

	alert(config: AlertPromptConfig): Promise<void> {
		return this.show(new AlertPrompt(config));
	}

	confirm(config: ConfirmationPromptConfig): Promise<boolean> {
		return this.show(new ConfirmationPrompt(config));
	}

	confirmDiscardUnsavedChanges(): Promise<boolean> {
		return this.show(new ConfirmDiscardUnsavedChangesPrompt());
	}
}
